import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, Link as LinkIcon, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Logo from '../components/Logo';

const QR = () => {
  const [url, setUrl] = useState('');
  const [size, setSize] = useState(512);
  const hiddenWrapRef = useRef(null);

  useEffect(() => {
    setUrl(window.location.origin);
  }, []);

  const download = () => {
    const canvas = hiddenWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `aura-lune-qr-${size}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const resetToOrigin = () => setUrl(window.location.origin);

  return (
    <div className="bg-[#F4EFE7] min-h-screen">
      <Navbar />

      <section className="pt-32 pb-20 max-w-[1000px] mx-auto px-8">
        <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-6">
          SHARE
        </p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#1E1B18] leading-[1.05] mb-4">
          Your booking QR code
        </h1>
        <p className="text-[15px] text-[#1E1B18]/70 leading-relaxed max-w-lg mb-12">
          Print this and place it at your desk, or share it in a post — scanning takes clients straight to your booking page.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-start">
          <div className="md:col-span-3 order-2 md:order-1 bg-[#FBF7F0] border border-[#1E1B18]/8 rounded-sm p-8">
            <label className="block text-[11px] tracking-[0.25em] text-[#1E1B18]/60 font-medium mb-3">
              LINK
            </label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 border border-[#1E1B18]/20 rounded-full px-4 py-2.5 focus-within:border-[#1E1B18]/60 transition-colors">
                <LinkIcon size={14} className="text-[#1E1B18]/40 shrink-0" />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://your-site.com"
                  className="flex-1 bg-transparent border-0 outline-none text-[14px] text-[#1E1B18] placeholder:text-[#1E1B18]/30"
                />
              </div>
              <button
                onClick={resetToOrigin}
                title="Use current site URL"
                className="inline-flex items-center gap-1.5 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 text-[#1E1B18] px-3 py-2.5 rounded-full text-[12px] font-medium transition-colors"
              >
                <RefreshCw size={13} /> Use this site
              </button>
            </div>

            <label className="block text-[11px] tracking-[0.25em] text-[#1E1B18]/60 font-medium mt-8 mb-3">
              SIZE
            </label>
            <div className="flex flex-wrap gap-2">
              {[256, 512, 1024].map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`px-4 py-2 rounded-full border text-[12px] font-medium transition-all ${
                    size === s
                      ? 'bg-[#0F0E0C] text-[#F4EFE7] border-[#0F0E0C]'
                      : 'bg-transparent text-[#1E1B18] border-[#1E1B18]/20 hover:border-[#1E1B18]/50'
                  }`}
                >
                  {s}px
                </button>
              ))}
            </div>

            <button
              onClick={download}
              disabled={!url}
              className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-[#0F0E0C] disabled:opacity-40 hover:bg-[#2A2622] text-[#F4EFE7] py-3.5 rounded-full text-[14px] font-medium transition-colors"
            >
              <Download size={15} /> Download PNG
            </button>
            <p className="text-[12px] text-[#1E1B18]/50 mt-4 leading-relaxed">
              Tip: print at least 5cm × 5cm on paper, or the 1024px file for signage.
            </p>
          </div>

          <div className="md:col-span-2 order-1 md:order-2 flex flex-col items-center">
            <div className="bg-white p-6 border border-[#1E1B18]/8 rounded-sm shadow-sm">
              {url ? (
                <QRCodeCanvas
                  value={url}
                  size={256}
                  bgColor="#FFFFFF"
                  fgColor="#0F0E0C"
                  level="H"
                  includeMargin={false}
                />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center text-[#1E1B18]/40 text-sm">
                  Enter a link
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Logo size={26} showText={false} />
              <div className="text-[10px] tracking-[0.3em] text-[#1E1B18]/60 font-medium">AURA LUNE</div>
            </div>
            {/* Hidden high-res canvas used only for downloading */}
            <div ref={hiddenWrapRef} style={{ position: 'absolute', left: '-9999px', top: 0 }} aria-hidden="true">
              <QRCodeCanvas
                value={url || 'https://example.com'}
                size={size}
                bgColor="#FFFFFF"
                fgColor="#0F0E0C"
                level="H"
                includeMargin
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QR;
